import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSaveFreelancerContact } from "@/hooks/use-freelancer-contacts";
import { isValidEmail } from "@/lib/email-validation";
import { normalizePhoneE164 } from "@/lib/phone-format";
import { CONTACT_ROLES } from "@/lib/freelancer-contacts";
import type { DBFreelancerContact, FreelancerContactRole } from "@/lib/db-types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  freelancerId: string;
  /** Satt ved redigering. */
  contact?: DBFreelancerContact | null;
  /** Plassering for en ny kontaktperson (sist). */
  nextSortOrder: number;
}

/** Ny eller redigert kontaktperson (VIB-2934). Forelderen gir dialogen ny key ved hver åpning. */
export function FreelancerContactDialog({ open, onOpenChange, freelancerId, contact, nextSortOrder }: Props) {
  const save = useSaveFreelancerContact();
  const [name, setName] = useState(contact?.name ?? "");
  const [role, setRole] = useState<FreelancerContactRole>(contact?.role ?? "contact");
  const [roleNote, setRoleNote] = useState(contact?.role_note ?? "");
  const [email, setEmail] = useState(contact?.email ?? "");
  const [phone, setPhone] = useState(contact?.phone ?? "");

  const emailError = email.trim() && !isValidEmail(email) ? "Ugyldig e-postadresse" : null;
  const phoneError = phone.trim() && !normalizePhoneE164(phone) ? "Skriv nummeret med landkode, for eksempel +46 70 123 45 67" : null;
  const canSave = name.trim().length > 0 && !emailError && !phoneError && !save.isPending;

  const submit = () =>
    save.mutate(
      { id: contact?.id, freelancerId, sortOrder: nextSortOrder, input: { name, role, role_note: roleNote, email, phone } },
      { onSuccess: () => onOpenChange(false) },
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{contact ? "Rediger kontaktperson" : "Ny kontaktperson"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); if (canSave) submit(); }} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="fc-name">Navn *</Label>
            <Input id="fc-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="fc-role">Rolle</Label>
              <Select value={role} onValueChange={(v) => setRole(v as FreelancerContactRole)}>
                <SelectTrigger id="fc-role" aria-label="Rolle"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONTACT_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {role === "other" && (
              <div className="space-y-1.5">
                <Label htmlFor="fc-role-note">Hvilken rolle?</Label>
                <Input id="fc-role-note" value={roleNote} onChange={(e) => setRoleNote(e.target.value)} placeholder="Booker" />
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fc-phone">Telefon</Label>
            <Input id="fc-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="912 34 567" aria-invalid={!!phoneError} />
            {phoneError && <p className="text-xs text-destructive">{phoneError}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fc-email">E-post</Label>
            <Input id="fc-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} aria-invalid={!!emailError} />
            {emailError && <p className="text-xs text-destructive">{emailError}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Avbryt</Button>
            <Button type="submit" disabled={!canSave}>Lagre kontaktperson</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
